// src/chatGPT/components/ChatHistorySidebar.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Plus,
  Loader,
  MoreHorizontal,
  Trash2,
  Star,
  FolderOpen,
  MessageSquare,
  Pencil
} from 'lucide-react';
import { TbLayoutSidebar, TbLayoutSidebarLeftCollapse } from 'react-icons/tb';
import toast from 'react-hot-toast';
import { db } from '../../firebase/config';
import { useAuthContext } from '../../hooks/useAuthContext';
import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot
} from 'firebase/firestore';
import { useChatHistory } from '../hooks/useChatHistory';
import { cn } from '../../utils/cn';
import { Dialog, DialogButton, DialogInput } from '../../components/ui/Dialog';
import { Tooltip } from '../../components/ui/Tooltip';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator
} from '../../components/ui/DropdownMenu';

export default function ChatHistorySidebar({
  isOpen,
  onToggle,
  currentChatId,
  onSelectChat,
  onNewChat
}) {
  const { deleteChat, toggleStarred, renameChat } = useChatHistory();
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingChatId, setDeletingChatId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [chatToDelete, setChatToDelete] = useState(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [chatToRename, setChatToRename] = useState(null);
  const [newTitle, setNewTitle] = useState('');
  const [openMenuId] = useState(null);

  // Use auth context to properly track user changes
  const { user } = useAuthContext();
  const prevUserIdRef = useRef(user?.uid);

  // Clear chats when user changes (logout/login with different account)
  useEffect(() => {
    if (prevUserIdRef.current !== user?.uid) {
      setChats([]);
      prevUserIdRef.current = user?.uid;
    }
  }, [user?.uid]);

  // Emit custom event when sidebar state changes so Navbar can respond
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('sidebar-state-changed', {
      detail: { isOpen }
    }));
  }, [isOpen]);

  // Load chat history from Firestore with real-time updates
  useEffect(() => {
    if (!user?.uid) return;

    setLoading(true);

    const chatsRef = collection(db, 'users', user.uid, 'chats');
    const q = query(chatsRef, orderBy('createdAt', 'desc'), limit(50));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const invalidIds = ['messages', 'metadata'];

      const chatList = snapshot.docs
        .filter(doc => !invalidIds.includes(doc.id))
        .map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            title: data.title || 'Untitled Chat',
            createdAt: data.createdAt,
            summary: data.preview || '',
            messages: data.messageCount,
            starred: data.starred || false
          };
        });

      setChats(chatList);
      setLoading(false);
    }, (error) => {
      console.error('Error loading chats:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user?.uid]);

  // Group chats by date, with starred at top
  const groupChatsByDate = (chats) => {
    const groups = {
      'Starred': [],
      'Recent': [],
      'Previous 30 Days': [],
      'Older': []
    };

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const twoWeeksAgo = new Date(today);
    twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
    const monthAgo = new Date(today);
    monthAgo.setDate(monthAgo.getDate() - 30);

    chats.forEach(chat => {
      if (!chat.createdAt) return;

      // Starred chats go to top group
      if (chat.starred) {
        groups['Starred'].push(chat);
        return;
      }

      const chatDate = chat.createdAt.toDate ? chat.createdAt.toDate() : chat.createdAt;

      if (chatDate >= twoWeeksAgo) {
        groups['Recent'].push(chat);
      } else if (chatDate >= monthAgo) {
        groups['Previous 30 Days'].push(chat);
      } else {
        groups['Older'].push(chat);
      }
    });

    return groups;
  };

  const chatGroups = groupChatsByDate(chats);

  // Handle chat deletion
  const handleDeleteChat = (chatId, chatTitle) => {
    setChatToDelete({ id: chatId, title: chatTitle });
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!chatToDelete) return;

    const { id: chatId } = chatToDelete;
    setDeletingChatId(chatId);
    setDeleteDialogOpen(false);

    const success = await deleteChat(chatId);

    if (success) {
      // Remove from local state immediately for instant UI update
      setChats(prev => prev.filter(c => c.id !== chatId));

      // If we deleted the current chat, navigate to new chat
      if (chatId === currentChatId) {
        onNewChat();
      }
      toast.success('Chat deleted');
    } else {
      toast.error('Failed to delete chat');
    }

    setDeletingChatId(null);
    setChatToDelete(null);
  };

  // Favorite/star a chat
  const handleFavoriteChat = async (chat) => {
    const newStarredValue = !chat.starred;
    const success = await toggleStarred(chat.id, chat.starred || false);

    if (success) {
      // Optimistic update
      setChats(prev => prev.map(c =>
        c.id === chat.id ? { ...c, starred: newStarredValue } : c
      ));
      toast.success(newStarredValue ? 'Added to starred' : 'Removed from starred');
    } else {
      toast.error('Failed to update starred status');
    }
  };

  // Rename a chat
  const handleRenameChat = (chatId, chatTitle) => {
    setChatToRename({ id: chatId, title: chatTitle });
    setNewTitle(chatTitle);
    setRenameDialogOpen(true);
  };

  const confirmRename = async () => {
    if (!chatToRename || !newTitle.trim()) return;

    const success = await renameChat(chatToRename.id, newTitle);

    if (success) {
      // Optimistic update
      setChats(prev => prev.map(c =>
        c.id === chatToRename.id ? { ...c, title: newTitle.trim() } : c
      ));
      toast.success('Chat renamed');
    } else {
      toast.error('Failed to rename chat');
    }

    setRenameDialogOpen(false);
    setChatToRename(null);
    setNewTitle('');
  };

  return (
    <>
      {/* Unified sidebar - collapses to rail, expands to full sidebar */}
      <div className={cn(
        "fixed left-0 top-0 h-screen bg-stone-100 border-r border-stone-200 z-50 print:hidden transition-[width] duration-300 ease-out select-none",
        isOpen ? "w-64" : "w-14"
      )}>
        <div className="flex flex-col h-full">
          {/* Header with toggle and new chat - always vertical, left-aligned */}
          <div className="flex flex-col items-start gap-3 pt-3 px-3 pb-4">
            {/* Sidebar toggle */}
            <Tooltip content={isOpen ? "Close sidebar" : "Open sidebar"} placement="right">
              <div
                onClick={onToggle}
                className="p-1.5 rounded-lg hover:bg-stone-200 transition-colors cursor-pointer"
                role="button"
                aria-label={isOpen ? "Close chat history" : "Open chat history"}
                aria-expanded={isOpen}
              >
                {isOpen ? (
                  <TbLayoutSidebarLeftCollapse className="h-6 w-6 text-stone-600" />
                ) : (
                  <TbLayoutSidebar className="h-6 w-6 text-stone-600" />
                )}
              </div>
            </Tooltip>

            {/* New chat button - shows label when expanded, icon only when collapsed */}
            {isOpen ? (
              <div
                onClick={onNewChat}
                className="flex items-center gap-2.5 w-full p-1.5 rounded-lg hover:bg-stone-200 transition-colors cursor-pointer group"
              >
                <div
                  className="h-6 w-6 rounded-md bg-stone-800 flex items-center justify-center flex-shrink-0 transition-transform duration-150 group-hover:scale-110 group-hover:-rotate-6"
                >
                  <Plus size={14} strokeWidth={2.5} className="text-stone-100" />
                </div>
                <span className="text-stone-700 text-sm font-medium whitespace-nowrap">New chat</span>
              </div>
            ) : (
              <Tooltip content="New chat" placement="right">
                <div
                  onClick={onNewChat}
                  className="p-1.5 rounded-lg hover:bg-stone-200 transition-colors cursor-pointer group"
                >
                  <div
                    className="h-6 w-6 rounded-md bg-stone-800 flex items-center justify-center transition-transform duration-150 group-hover:scale-110 group-hover:-rotate-6"
                  >
                    <Plus size={14} strokeWidth={2.5} className="text-stone-100" />
                  </div>
                </div>
              </Tooltip>
            )}
          </div>

          {/* Chat list - only when expanded */}
          {isOpen && (
            <div className="flex-1 overflow-y-auto">
              <div className="w-[248px]">
                {loading ? (
                  <div className="flex items-center justify-center p-8">
                    <Loader size={24} className="animate-spin text-stone-400" />
                  </div>
                ) : chats.length === 0 ? (
                  <div className="text-center p-8 text-stone-500">
                    <MessageSquare size={32} className="mx-auto mb-3 opacity-50" />
                    <p className="text-sm">No conversations yet</p>
                  </div>
                ) : (
                  <div className="p-2 pb-52">
                    {Object.entries(chatGroups).map(([groupName, groupChats]) => {
                      if (groupChats.length === 0) return null;

                      return (
                        <div key={groupName} className="mb-4 overflow-hidden">
                          <h3 className="text-xs font-medium text-stone-500 uppercase px-3 mb-2">
                            {groupName}
                          </h3>
                          {groupChats.map((chat) => (
                            <div
                              key={chat.id}
                              className={cn(
                                "group relative w-full text-left p-2 rounded-lg mb-0.5 transition-colors cursor-pointer overflow-hidden",
                                "hover:bg-stone-200",
                                (currentChatId === chat.id || openMenuId === chat.id) && "bg-stone-200",
                                deletingChatId === chat.id && "opacity-50 pointer-events-none"
                              )}
                              onClick={() => {
                                if (deletingChatId !== chat.id) {
                                  onSelectChat(chat.id);
                                }
                              }}
                            >
                              <div className="flex items-center gap-2 relative">
                                <div className="flex-1 min-w-0">
                                  {/* Title - always truncate */}
                                  <h4 className="font-medium text-sm text-stone-800 truncate">
                                    {chat.title}
                                  </h4>
                                </div>

                                {/* Gradient fade + solid background for menu button area */}
                                <div
                                  className={cn(
                                    "absolute right-0 top-0 bottom-0 w-20 pointer-events-none transition-opacity flex",
                                    (currentChatId === chat.id || openMenuId === chat.id)
                                      ? "opacity-100"
                                      : "opacity-0 group-hover:opacity-100"
                                  )}
                                >
                                  {/* Gradient fade portion */}
                                  <div className="w-12 bg-gradient-to-r from-transparent to-stone-200" />
                                  {/* Solid background for menu button */}
                                  <div className="w-8 bg-stone-200" />
                                </div>

                                {/* Dropdown menu - visible on hover/selected */}
                                <div
                                  className={cn(
                                    "absolute right-0 top-1/2 -translate-y-1/2 flex items-center transition-opacity",
                                    (currentChatId === chat.id || openMenuId === chat.id)
                                      ? "opacity-100"
                                      : "opacity-0 group-hover:opacity-100"
                                  )}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <DropdownMenu>
                                    <DropdownMenuTrigger
                                      className={cn(
                                        "h-7 w-7 p-0 flex items-center justify-center rounded hover:bg-stone-300",
                                        openMenuId === chat.id && "bg-stone-300"
                                      )}
                                      disabled={deletingChatId === chat.id}
                                      aria-label="Chat options"
                                    >
                                      {deletingChatId === chat.id ? (
                                        <Loader size={14} className="animate-spin" />
                                      ) : (
                                        <MoreHorizontal size={16} />
                                      )}
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuItem onClick={() => onSelectChat(chat.id)}>
                                        <FolderOpen className="h-4 w-4" />
                                        Open
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => handleRenameChat(chat.id, chat.title)}>
                                        <Pencil className="h-4 w-4" />
                                        Rename
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => handleFavoriteChat(chat)}>
                                        <Star className={cn("h-4 w-4", chat.starred && "fill-current")} />
                                        {chat.starred ? 'Unstar' : 'Star'}
                                      </DropdownMenuItem>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem
                                        onClick={() => handleDeleteChat(chat.id, chat.title)}
                                        destructive
                                      >
                                        <Trash2 className="h-4 w-4" />
                                        Delete
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setChatToDelete(null);
        }}
        title="Delete chat?"
        footer={
          <>
            <DialogButton
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false);
                setChatToDelete(null);
              }}
            >
              Cancel
            </DialogButton>
            <DialogButton
              variant="destructive"
              onClick={confirmDelete}
            >
              Delete
            </DialogButton>
          </>
        }
      >
        <p>
          This will permanently delete "{chatToDelete?.title}". This action cannot be undone.
        </p>
      </Dialog>

      {/* Rename dialog */}
      <Dialog
        open={renameDialogOpen}
        onClose={() => {
          setRenameDialogOpen(false);
          setChatToRename(null);
          setNewTitle('');
        }}
        title="Rename chat"
        footer={
          <>
            <DialogButton
              variant="outline"
              onClick={() => {
                setRenameDialogOpen(false);
                setChatToRename(null);
                setNewTitle('');
              }}
            >
              Cancel
            </DialogButton>
            <DialogButton
              onClick={confirmRename}
              disabled={!newTitle.trim()}
            >
              Save
            </DialogButton>
          </>
        }
      >
        <DialogInput
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          placeholder="Chat title"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              confirmRename();
            }
          }}
        />
      </Dialog>
    </>
  );
}
